import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import {
  ChevronLeft,
  Edit,
  Loader2,
  BookOpen,
  Eye,
  Scissors,
  Download,
  Globe,
  Award,
  FileText,
  Sparkles,
  BookImage
} from 'lucide-react'
import { apiClient } from '../../lib/api'
import { TranslationMatrix } from './TranslationMatrix'
import { BookSplitter } from './BookSplitter'
import { GenerateCoverDialog } from './GenerateCoverDialog'
import { GenerateDescriptionDialog } from './GenerateDescriptionDialog'
import { GenerateDescriptionCoverDialog } from './GenerateDescriptionCoverDialog'
import { getLanguageByCode } from '../../lib/constants/languages'

export function AdminBookDetail() {
  const { t } = useTranslation('common')
  const { bookId } = useParams<{ bookId: string }>()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [book, setBook] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showSplitter, setShowSplitter] = useState(false)
  const [showGenerateCoverDialog, setShowGenerateCoverDialog] = useState(false)
  const [showGenerateDescriptionDialog, setShowGenerateDescriptionDialog] = useState(false)
  const [showGenerateDescriptionCoverDialog, setShowGenerateDescriptionCoverDialog] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('')
  const [selectedTranslation, setSelectedTranslation] = useState<any>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadingOriginal, setDownloadingOriginal] = useState(false)

  // Get return tab from URL or default to 'books'
  const returnTab = searchParams.get('returnTab') || 'books'
  const getReturnUrl = () => {
    return `/admin/management?tab=${returnTab}`
  }

  useEffect(() => {
    loadBook()
  }, [bookId])

  const loadBook = async () => {
    if (!bookId) return

    try {
      setLoading(true)
      const data = await apiClient.getAdminBook(bookId)
      setBook(data.book)
      
      // Auto-select first available language if available
      const availableLanguages = data.book.available_languages || []
      if (availableLanguages.length > 0) {
        setSelectedLanguage(availableLanguages[0])
      }
    } catch (error) {
      console.error('Failed to load book:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!bookId || !selectedTranslation || !user) {
      return
    }

    try {
      setDownloading(true)
      await apiClient.downloadLibraryBook(bookId, selectedTranslation.id)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Download failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadOriginal = async () => {
    if (!bookId || !user) {
      return
    }

    try {
      setDownloadingOriginal(true)
      await apiClient.downloadLibraryBookOriginal(bookId)
    } catch (error) {
      console.error('Download original failed:', error)
      alert('Download failed. Please try again.')
    } finally {
      setDownloadingOriginal(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading book...</p>
        </div>
      </div>
    )
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Book not found</h2>
          <Button onClick={() => navigate(getReturnUrl())}>Back to Management</Button>
        </div>
      </div>
    )
  }

  // Flatten translations for the matrix
  const allTranslations = book.translations 
    ? Object.values(book.translations).flat() as any[]
    : []

  // Get translations for selected language
  const selectedTranslations = selectedLanguage ? book.translations?.[selectedLanguage] || [] : []

  // Check if book is EPUB (can be split)
  const isEpub = book.format && book.format.toLowerCase().includes('epub')

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode
  }
  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      {/* Book Splitter Modal */}
      {showSplitter && (
        <BookSplitter
          bookId={bookId!}
          bookTitle={book.title_international}
          onClose={() => setShowSplitter(false)}
          onComplete={() => {
            setShowSplitter(false)
            // Optionally navigate back to book list
            navigate(getReturnUrl())
          }}
        />
      )}

      {/* Generate Cover Dialog */}
      {showGenerateCoverDialog && (
        <GenerateCoverDialog
          books={[{
            id: book.id,
            title_international: book.title_international,
            author_international: book.author_international,
            description: book.description
          }]}
          onClose={() => setShowGenerateCoverDialog(false)}
          onSuccess={() => {
            setShowGenerateCoverDialog(false)
            loadBook()
          }}
        />
      )}

      {/* Generate Description Dialog */}
      {showGenerateDescriptionDialog && (
        <GenerateDescriptionDialog
          books={[{
            id: book.id,
            title_international: book.title_international,
            author_international: book.author_international,
            description: book.description
          }]}
          onClose={() => setShowGenerateDescriptionDialog(false)}
          onSuccess={() => {
            setShowGenerateDescriptionDialog(false)
            loadBook()
          }}
        />
      )}

      {/* Generate Description + Cover Dialog */}
      {showGenerateDescriptionCoverDialog && (
        <GenerateDescriptionCoverDialog
          books={[{
            id: book.id,
            title_international: book.title_international,
            author_international: book.author_international,
            description: book.description
          }]}
          onClose={() => setShowGenerateDescriptionCoverDialog(false)}
          onSuccess={() => {
            setShowGenerateDescriptionCoverDialog(false)
            loadBook()
          }}
        />
      )}
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(getReturnUrl())}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Management
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-2 h-8 bg-primary rounded-full"></div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{book.title_international}</h1>
                <p className="text-sm text-muted-foreground">
                  by {book.author_international}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/library/books/${bookId}`)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View Public
              </Button>
              {isEpub && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSplitter(true)}
                  title="Split this book into separate books by chapter/section"
                >
                  <Scissors className="mr-2 h-4 w-4" />
                  Split Book
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGenerateDescriptionDialog(true)}
                title="Generate AI description for this book"
              >
                <FileText className="mr-2 h-4 w-4" />
                Generate Description
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGenerateCoverDialog(true)}
                title="Generate AI cover for this book"
              >
                <BookImage className="mr-2 h-4 w-4" />
                Generate AI Cover
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGenerateDescriptionCoverDialog(true)}
                title="Generate AI description first, then cover using that description"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Description+Cover
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate(`/admin/books/${bookId}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Book
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Book Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardContent className="p-6">
                {/* Cover */}
                <div className="aspect-[2/3] mb-6 bg-muted rounded-lg flex items-center justify-center overflow-hidden max-w-[200px] mx-auto">
                  {book.cover_url ? (
                    <img
                      src={book.cover_url}
                      alt={book.title_international}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <BookOpen className="h-12 w-12 text-muted-foreground" />
                  )}
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Original Title</h3>
                    <p className="text-foreground">{book.title_original}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Original Author</h3>
                    <p className="text-foreground">{book.author_original}</p>
                  </div>

                  {book.original_language && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Original Language</h3>
                      <p className="text-foreground uppercase">{book.original_language}</p>
                    </div>
                  )}

                  {book.genre && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Genre</h3>
                      <p className="text-foreground">{book.genre}</p>
                    </div>
                  )}

                  {book.estimated_pages && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Length</h3>
                      <p className="text-foreground flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        ~{book.estimated_pages} pages
                      </p>
                    </div>
                  )}

                  {book.description && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
                      <p className="text-sm text-foreground">{book.description}</p>
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                    {book.is_hidden ? (
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        Hidden from users
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Visible to users
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Download Options and Translation Matrix */}
          <div className="lg:col-span-2 space-y-6">
            {/* Translation Matrix */}
            <TranslationMatrix
              bookId={bookId!}
              translations={allTranslations}
              onRefresh={loadBook}
            />

            {/* Download Section */}
            <Card>
              <CardHeader>
                <CardTitle className="download-options-title flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Download Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Language Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                    <Globe className="h-4 w-4" />
                    Select Language
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.keys(book.translations || {}).map((lang) => {
                      const isAvailable = (book.available_languages || []).includes(lang)
                      return (
                        <button
                          key={lang}
                          onClick={() => {
                            setSelectedLanguage(lang)
                            setSelectedTranslation(null)
                          }}
                          className={`lang-select-btn p-3 border-2 rounded-lg text-sm font-medium transition-colors relative ${
                            selectedLanguage === lang
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/50 text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            {getLanguageDisplayName(lang)}
                            {!isAvailable && (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Level Selection */}
                {selectedLanguage && selectedTranslations.length > 0 && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                      <Award className="h-4 w-4" />
                      Select Level or Version
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedTranslations.map((trans: any) => {
                        const levelLabel = trans.is_human_translation 
                          ? 'Original Translation'
                          : trans.cefr_level || 'Unknown'
                        const isAvailable = trans.status === 'available'
                        
                        return (
                          <button
                            key={trans.id}
                            onClick={() => isAvailable && setSelectedTranslation(trans)}
                            disabled={!isAvailable}
                            className={`level-select-btn p-3 border-2 rounded-lg text-sm font-medium transition-colors relative ${
                              selectedTranslation?.id === trans.id
                                ? 'border-primary bg-primary/10 text-primary'
                                : isAvailable
                                ? 'border-border hover:border-primary/50 text-foreground'
                                : 'border-border/50 text-muted-foreground cursor-not-allowed opacity-60'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1">
                              {levelLabel}
                              {!isAvailable && (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              )}
                            </div>
                            <div className="text-xs mt-1 flex items-center justify-center gap-1">
                              <span className={isAvailable ? 'text-muted-foreground' : ''}>
                                {trans.file_format.toUpperCase()}
                              </span>
                              {!isAvailable && (
                                <span className="text-xs">({trans.status})</span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Download Button */}
                <div className="pt-4">
                  {selectedTranslation ? (
                    selectedTranslation.status === 'available' ? (
                      <Button
                        onClick={handleDownload}
                        disabled={downloading}
                        className="download-btn w-full"
                        size="lg"
                      >
                        <Download className="mr-2 h-5 w-5" />
                        {downloading ? 'Downloading...' : 'Download'}
                      </Button>
                    ) : (
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <p className="text-sm font-medium text-foreground">
                            Translation in Progress
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          This translation is currently being processed. Please check back later.
                        </p>
                      </div>
                    )
                  ) : (
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {t('library.selectLanguageAndLevel')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Download Original Book */}
                {book.original_file_path && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-3">
                      Original Book
                    </p>
                    <Button
                      onClick={handleDownloadOriginal}
                      disabled={downloadingOriginal}
                      variant="outline"
                      className="download-original-btn w-full"
                      size="lg"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      {downloadingOriginal ? 'Downloading...' : 'Download Original'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  )
}

